import { registerIndicator, type Indicator, type KLineData } from 'klinecharts'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { isNum } from './overlayUtils'
import {
  computeSizingReadout,
  computeStopSeries,
  type ManualLevel,
  type SizingDirection,
  type SizingReadout,
  type SizingSource,
} from './positionSizing'

/**
 * POSITION_SIZE — a sizing READOUT, not a plotted series.
 *
 * Given a direction, a dollar risk, and a reference-level source, it computes
 * the share size implied by the distance between the last close and the
 * nearest RELEVANT level from that source (the adverse-side rule — see
 * positionSizing.ts and the 2026-08-24 spec).
 *
 * On the canvas it draws exactly one thing: the stop line (red dashed by
 * default). Series-shaped sources (sma/ema/vwap/atr) track their curve bar by
 * bar; scalar sources render flat at the bound level. The registered template
 * keeps `figures: []` so the stop never stretches the price pane's y-axis
 * (the VPVR/HTF pattern: the CATALOG declares the line figure, which lights
 * up the Style tab, and `drawStopLine` reads `styles.lines[0]`).
 *
 * The readout itself is not canvas-drawn. The calc pushes it through
 * `extendData.onReadout`, which ChartManager relays onto its event bus as
 * `positionSize:readout`; the React layer (PositionSizeReadout.tsx) renders
 * the pill — compact by default, full detail on hover. React owns the pill so
 * hover works without canvas hit-testing, and updates ride React state instead
 * of canvas repaint timing.
 *
 * Manual mode reads the user's own horizontal lines / rays: ChartManager
 * snapshots them into `extendData.manualLevels` on every overlay change
 * (draw-end / drag / remove / restore), which re-runs calc via klinecharts'
 * calcFlag — the indicator itself never touches the overlay store.
 */

export const POSITION_SIZE_NAME = 'POSITION_SIZE'

export interface SizingParams {
  direction: SizingDirection
  riskDollars: number
  /** Ceiling on capital deployed. 0 means no ceiling. */
  maxPositionDollars: number
  source: SizingSource
  maPeriod: number
}

export const DEFAULT_SIZING_PARAMS: SizingParams = {
  direction: 'long',
  riskDollars: 100,
  // Off by default: an account ceiling is personal, and silently capping a
  // size the trader did not ask to cap would be worse than not having it.
  maxPositionDollars: 0,
  source: 'manual',
  maPeriod: 9,
}

/** What the calc hands to the React pill on every recalc. */
export interface PositionSizeReadoutPayload {
  readout: SizingReadout
  direction: SizingDirection
  riskDollars: number
  maxPositionDollars: number
  source: SizingSource
  precision: number
}

/** What ChartManager pushes into the indicator's extendData. */
export interface PositionSizeExtendData {
  manualLevels?: ManualLevel[]
  timezone?: string
  onReadout?: (payload: PositionSizeReadoutPayload) => void
}

const DIRECTIONS: readonly SizingDirection[] = ['long', 'short']
const SOURCES: readonly SizingSource[] = [
  'manual',
  'pivot',
  'pdh',
  'pdl',
  'sma',
  'ema',
  'vwap',
  'atr',
]

/**
 * Positional calcParams tuple → validated params. Any malformed slot falls
 * back to its default — old persisted payloads must never crash the calc.
 */
export function parseSizingParams(calcParams: unknown[] | undefined): SizingParams {
  const [direction, risk, source, maPeriod, maxPosition] = calcParams ?? []
  return {
    direction: DIRECTIONS.includes(direction as SizingDirection)
      ? (direction as SizingDirection)
      : DEFAULT_SIZING_PARAMS.direction,
    riskDollars: isNum(risk) && risk > 0 ? risk : DEFAULT_SIZING_PARAMS.riskDollars,
    source: SOURCES.includes(source as SizingSource)
      ? (source as SizingSource)
      : DEFAULT_SIZING_PARAMS.source,
    maPeriod:
      isNum(maPeriod) && maPeriod >= 1 ? Math.floor(maPeriod) : DEFAULT_SIZING_PARAMS.maPeriod,
    // Appended last in the tuple, so layouts persisted before the cap existed
    // read `undefined` here and fall back to 0 (no ceiling).
    maxPositionDollars:
      isNum(maxPosition) && maxPosition > 0
        ? maxPosition
        : DEFAULT_SIZING_PARAMS.maxPositionDollars,
  }
}

/** Default stop-line look when no styles have been applied yet: the app's
 *  stop amber (positionPlanOverlay STOP_COLOR - risk line in both directions), dashed
 *  so it reads as a stop and not another traded series. */
const STOP_LINE_COLOR = '#f59e0b'
const STOP_LINE_DASH = [4, 4]

interface StopLineStyle {
  color?: string
  size?: number
  style?: string
  dashedValue?: number[]
  visible?: boolean
}

export interface StopLineDrawParams {
  ctx: CanvasRenderingContext2D
  indicator: {
    result: Array<{ stop?: number }>
    styles?: { lines?: StopLineStyle[] } | null
  }
  visibleRange: { from: number; to: number }
  barSpace: { halfBar: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  yAxis: { convertToPixel: (value: number) => number }
}

/**
 * Custom renderer for the stop line: polylines through the per-bar stop
 * values, breaking wherever the series has no value (warm-up, unbound
 * scalar). An isolated one-bar stop still shows as a bar-wide tick so a
 * single valid bar is never invisible.
 */
export function drawStopLine(params: StopLineDrawParams): boolean {
  const { ctx, indicator, visibleRange, barSpace, xAxis, yAxis } = params
  const result = indicator.result
  const style = indicator.styles?.lines?.[0]
  if (style?.visible === false) return true
  const from = Math.max(0, visibleRange.from)
  const to = Math.min(result.length, visibleRange.to)
  if (to <= from) return true

  ctx.save()
  ctx.strokeStyle = style?.color ?? STOP_LINE_COLOR
  ctx.lineWidth = style?.size ?? 1
  // Dashed unless the user explicitly picked solid; with no styles at all
  // (chart not yet styled) the default look is the dashed stop red.
  ctx.setLineDash(
    style
      ? style.style === 'dashed'
        ? (style.dashedValue ?? STOP_LINE_DASH)
        : []
      : STOP_LINE_DASH,
  )

  let run: Array<[number, number]> = []
  const flush = () => {
    if (run.length === 0) return
    ctx.beginPath()
    if (run.length === 1) {
      const [x, y] = run[0]
      ctx.moveTo(x - barSpace.halfBar, y)
      ctx.lineTo(x + barSpace.halfBar, y)
    } else {
      ctx.moveTo(run[0][0], run[0][1])
      for (let i = 1; i < run.length; i++) ctx.lineTo(run[i][0], run[i][1])
    }
    ctx.stroke()
    run = []
  }
  for (let i = from; i < to; i++) {
    const stop = result[i]?.stop
    if (stop == null || !isNum(stop)) {
      flush()
      continue
    }
    run.push([xAxis.convertToPixel(i), yAxis.convertToPixel(stop)])
  }
  flush()
  ctx.restore()

  // Returning true skips klinecharts' default figure rendering.
  return true
}

/** Register the POSITION_SIZE indicator with klinecharts (once). */
export const registerPositionSizeIndicator = registerOnce(() => {
  registerIndicator({
    name: POSITION_SIZE_NAME,
    shortName: 'SIZE',
    series: 'price' as never,
    precision: 2,
    calcParams: [
      DEFAULT_SIZING_PARAMS.direction,
      DEFAULT_SIZING_PARAMS.riskDollars,
      DEFAULT_SIZING_PARAMS.source,
      DEFAULT_SIZING_PARAMS.maPeriod,
      DEFAULT_SIZING_PARAMS.maxPositionDollars,
    ],
    figures: [],
    calc: (dataList: KLineData[], indicator: Indicator) => {
      const params = parseSizingParams(indicator.calcParams as unknown[])
      const extend = (indicator.extendData as PositionSizeExtendData | undefined) ?? {}
      const precision = isNum(indicator.precision) ? indicator.precision : 2
      const readout = computeSizingReadout({
        ...params,
        bars: dataList,
        manualLevels: extend.manualLevels ?? [],
        timezone: extend.timezone,
        tick: Math.pow(10, -precision),
      })
      extend.onReadout?.({
        readout,
        direction: params.direction,
        riskDollars: params.riskDollars,
        maxPositionDollars: params.maxPositionDollars,
        source: params.source,
        precision,
      })
      const stops = computeStopSeries(
        {
          ...params,
          bars: dataList,
          manualLevels: extend.manualLevels ?? [],
          timezone: extend.timezone,
          tick: Math.pow(10, -precision),
        },
        readout,
      )
      return dataList.map((_, i) => ({ stop: stops[i] }))
    },
    draw: drawStopLine as never,
  } as IndicatorTemplateParam)
})
